import { useEffect, useState } from 'react';

const DEFAULT_DURATION_MS = 460;

export type PresencePhase = 'enter' | 'idle' | 'exit';

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Keeps a component mounted through exit animations.
 */
export function useAnimatedPresence(
  visible: boolean,
  durationMs = DEFAULT_DURATION_MS
): { mounted: boolean; phase: PresencePhase } {
  const [mounted, setMounted] = useState(visible);
  const [phase, setPhase] = useState<PresencePhase>(visible ? 'enter' : 'exit');

  useEffect(() => {
    if (prefersReducedMotion()) {
      setMounted(visible);
      setPhase(visible ? 'idle' : 'exit');
      return undefined;
    }

    if (visible) {
      setMounted(true);
      setPhase('enter');
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setPhase('idle'));
      });
      return () => cancelAnimationFrame(raf);
    }

    if (mounted) {
      setPhase('exit');
      const timer = window.setTimeout(() => {
        setMounted(false);
      }, durationMs);
      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, [visible, mounted, durationMs]);

  return { mounted, phase };
}
